import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { verseService } from '../../../services/supabaseService';
import { checkAndGrantAwards } from '../../../services/awardService';
import { trackVersePractice } from '../../../services/streakService';

/**
 * ProgressiveRevealMode
 *
 * subMode = 'reveal'  (Mode 1): Words are hidden; user reveals them one by one in order.
 *                               After all revealed, user recites and marks complete.
 *
 * subMode = 'blank'   (Mode 2): Full verse shown first. User reads/recites it.
 *                               Then words are randomly removed one by one until all blank.
 *                               User must recite the full verse from memory at each stage.
 */

/** Strip paragraph markers (¶ § etc.) and all punctuation, then normalise whitespace */
const cleanVerseText = (text) => {
  if (!text) return '';
  return text?.replace(/[¶§]/g, '')?.replace(/[^\w\s]/g, '')?.replace(/\s+/g, ' ')?.trim();
};

const ProgressiveRevealMode = ({
  verse,
  onComplete,
  onNext,
  verseIndex = 0,
  totalVerses = 1,
  textSizePx = '16px',
}) => {
  const [subMode, setSubMode] = useState(null); // null = pick mode, 'reveal', 'blank'
  const [stage, setStage] = useState('reading'); // reading | reciting | done
  const [revealedCount, setRevealedCount] = useState(0);
  const [hiddenIndices, setHiddenIndices] = useState([]); // for blank mode: indices hidden so far
  const [userInput, setUserInput] = useState('');
  const [inputStatus, setInputStatus] = useState('idle'); // idle | correct | incorrect
  const [showFullVerse, setShowFullVerse] = useState(false);

  // Add to library state
  const [userId, setUserId] = useState(null);
  const [libraryStatus, setLibraryStatus] = useState('idle'); // idle | checking | already_added | adding | added | error
  const [libraryError, setLibraryError] = useState('');

  // Track completion for awards + library
  const [isCompleted, setIsCompleted] = useState(false);

  const cleanedText = cleanVerseText(verse?.text);
  const tokens = cleanedText ? cleanedText?.split(/\s+/)?.filter(Boolean) : [];

  // Get current user on mount
  useEffect(() => {
    supabase?.auth?.getSession()?.then(({ data }) => {
      setUserId(data?.session?.user?.id || null);
    });
  }, []);

  // Reset on verse change
  useEffect(() => {
    setSubMode(null);
    setStage('reading');
    setRevealedCount(0);
    setHiddenIndices([]);
    setUserInput('');
    setInputStatus('idle');
    setShowFullVerse(false);
    setIsCompleted(false);
    setLibraryStatus('idle');
    setLibraryError('');
  }, [verse?.id, verse?.text]);

  // When completed, check library status
  useEffect(() => {
    if (isCompleted && userId && verse?.reference) {
      checkIfInLibrary();
    }
  }, [isCompleted, userId, verse?.reference]);

  const checkIfInLibrary = async () => {
    if (!userId || !verse?.reference) return;
    setLibraryStatus('checking');
    try {
      const { data } = await supabase
        ?.from('verses')
        ?.select('id')
        ?.eq('user_id', userId)
        ?.eq('reference', verse?.reference)
        ?.maybeSingle();
      setLibraryStatus(data ? 'already_added' : 'idle');
    } catch {
      setLibraryStatus('idle');
    }
  };

  const handleAddToLibrary = async () => {
    if (!userId || !verse?.reference) return;
    setLibraryStatus('adding');
    setLibraryError('');
    try {
      const { error } = await verseService?.createVerseFromBible(userId, verse?.reference);
      if (error) {
        if (error?.message?.includes('already')) {
          setLibraryStatus('already_added');
        } else {
          setLibraryError(error?.message || 'Failed to add verse');
          setLibraryStatus('error');
        }
      } else {
        setLibraryStatus('added');
      }
    } catch (err) {
      setLibraryError(err?.message || 'Failed to add verse');
      setLibraryStatus('error');
    }
  };

  // ── Mode 1: Reveal ────────────────────────────────────────────────────────

  const revealNext = () => {
    if (revealedCount < tokens?.length) {
      setRevealedCount(prev => prev + 1);
    }
  };

  const revealAll = () => setRevealedCount(tokens?.length);

  // ── Mode 2: Blank ─────────────────────────────────────────────────────────

  // Build a shuffled list of indices to hide progressively
  const buildHideOrder = useCallback(() => {
    const indices = tokens?.map((_, i) => i);
    for (let i = indices?.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices?.[j], indices?.[i]];
    }
    return indices;
  }, [tokens?.length]);

  const startBlankMode = () => {
    setSubMode('blank');
    setStage('reading'); // show full verse first
    setHiddenIndices([]);
    setUserInput('');
    setInputStatus('idle');
  };

  const startRevealMode = () => {
    setSubMode('reveal');
    setRevealedCount(0);
    setUserInput('');
    setInputStatus('idle');
  };

  // Remove one more word randomly
  const removeNextWord = () => {
    const order = buildHideOrder();
    const alreadyHidden = new Set(hiddenIndices);
    const next = order?.find(i => !alreadyHidden?.has(i));
    if (next !== undefined) {
      setHiddenIndices(prev => [...prev, next]);
      setUserInput('');
      setInputStatus('idle');
    }
  };

  // Check recitation input — strip punctuation from both sides before comparing
  const checkRecitation = () => {
    const clean = (s) => s?.toLowerCase()?.replace(/[^\w\s]/g, '')?.replace(/\s+/g, ' ')?.trim();
    const isCorrect = clean(userInput) === clean(tokens?.join(' '));
    setInputStatus(isCorrect ? 'correct' : 'incorrect');
  };

  const handleFinish = async () => {
    setIsCompleted(true);

    // Grant awards and track streak if user is logged in
    if (userId) {
      try {
        // Look up real session count so award thresholds are accurate
        const { count: totalSessions } = await supabase
          ?.from('user_review_sessions')
          ?.select('id', { count: 'exact', head: true })
          ?.eq('user_id', userId)
          ?.eq('is_complete', true);

        await checkAndGrantAwards(userId, 'SESSION_COMPLETED', {
          totalSessions: (totalSessions || 0) + 1,
        });
        await checkAndGrantAwards(userId, 'SESSION_MILESTONE', {
          totalSessions: (totalSessions || 0) + 1,
        });

        // Track streak activity
        await trackVersePractice(userId, 1);
      } catch (err) {
        console.warn('Award/streak grant error:', err);
      }
    }

    onComplete?.({ correct: true, subMode });
    // Don't call onNext here — let the completion UI show first
  };

  const handleNext = () => {
    onNext?.();
  };

  const progress = totalVerses > 1 ? (verseIndex / totalVerses) * 100 : 0;

  // ── Add to Library card (reused in both modes) ────────────────────────────
  const AddToLibraryCard = () => {
    if (!userId) return null;
    return (
      <div className="rounded-xl border border-border bg-card px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Icon name="BookMarked" size={18} className="text-primary flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground leading-tight">Add to Your Library</p>
            <p className="text-xs text-muted-foreground truncate">{verse?.reference}</p>
          </div>
        </div>

        {libraryStatus === 'checking' && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Icon name="Loader" size={14} className="animate-spin" />
            <span>Checking…</span>
          </div>
        )}
        {libraryStatus === 'idle' && (
          <button
            onClick={handleAddToLibrary}
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90 active:scale-95 transition-all"
          >
            <Icon name="Plus" size={14} />
            Add Verse
          </button>
        )}
        {libraryStatus === 'adding' && (
          <div className="flex items-center gap-1.5 text-xs text-primary">
            <Icon name="Loader" size={14} className="animate-spin" />
            <span>Adding…</span>
          </div>
        )}
        {libraryStatus === 'added' && (
          <div className="flex items-center gap-1.5 text-xs text-success font-semibold">
            <Icon name="CheckCircle" size={14} />
            <span>Added!</span>
          </div>
        )}
        {libraryStatus === 'already_added' && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Icon name="Check" size={14} />
            <span>In library</span>
          </div>
        )}
        {libraryStatus === 'error' && (
          <div className="flex flex-col items-end gap-1">
            <span className="text-xs text-destructive">{libraryError || 'Error'}</span>
            <button onClick={handleAddToLibrary} className="text-xs text-primary underline">
              Retry
            </button>
          </div>
        )}
      </div>
    );
  };

  // ── Sub-mode picker ───────────────────────────────────────────────────────
  if (!subMode) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <div className="bg-card border-b border-border px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-foreground">{verse?.reference}</span>
              {totalVerses > 1 && (
                <span className="text-xs text-muted-foreground">{verseIndex + 1} / {totalVerses}</span>
              )}
            </div>
            {totalVerses > 1 && (
              <div className="w-full bg-muted rounded-full h-1.5">
                <div className="bg-primary h-1.5 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 px-4 py-6 max-w-2xl mx-auto w-full flex flex-col gap-6">
          <div className="flex items-center gap-2 text-success">
            <Icon name="Eye" size={18} />
            <span className="text-sm font-semibold uppercase tracking-wide">Progressive Reveal</span>
          </div>

          <div className="bg-muted/40 border border-border rounded-xl p-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Verse</p>
            <p className="text-foreground leading-relaxed" style={{ fontSize: textSizePx }}>{cleanedText}</p>
            <p className="text-xs text-muted-foreground mt-2 font-medium">— {verse?.reference}</p>
          </div>

          <p className="text-sm text-muted-foreground text-center">Choose a reveal mode:</p>

          <div className="grid gap-4">
            {/* Mode 1 */}
            <button
              onClick={startRevealMode}
              className="text-left p-5 rounded-xl border-2 border-border bg-card hover:border-success/60 hover:bg-success/5 transition-all duration-200 active:scale-[0.98] group"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-success/15 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <Icon name="Eye" size={22} color="var(--color-success)" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Mode 1 — Reveal Word by Word</h3>
                  <p className="text-sm text-muted-foreground">
                    Words are hidden. Reveal them one at a time in order as you memorize the verse.
                  </p>
                </div>
              </div>
            </button>

            {/* Mode 2 */}
            <button
              onClick={startBlankMode}
              className="text-left p-5 rounded-xl border-2 border-border bg-card hover:border-primary/60 hover:bg-primary/5 transition-all duration-200 active:scale-[0.98] group"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <Icon name="EyeOff" size={22} color="var(--color-primary)" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Mode 2 — Fade to Blank</h3>
                  <p className="text-sm text-muted-foreground">
                    Read the full verse, then words are randomly removed one by one until you recite it entirely from memory.
                  </p>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Mode 1: Reveal word by word ───────────────────────────────────────────
  if (subMode === 'reveal') {
    const allRevealed = revealedCount >= tokens?.length;

    return (
      <div className="min-h-screen bg-background flex flex-col">
        {/* Top bar */}
        <div className="bg-card border-b border-border px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
          <button
            onClick={() => setSubMode(null)}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
          >
            <Icon name="ArrowLeft" size={18} />
          </button>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-foreground">{verse?.reference}</span>
              <span className="text-xs text-muted-foreground">{revealedCount}/{tokens?.length} words</span>
            </div>
            <div className="w-full bg-muted rounded-full h-1.5">
              <div
                className="bg-success h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${(revealedCount / tokens?.length) * 100}%` }}
              />
            </div>
          </div>
        </div>
        <div className="flex-1 px-4 py-5 flex flex-col gap-5 max-w-2xl mx-auto w-full">
          <div className="flex items-center gap-2 text-success">
            <Icon name="Eye" size={18} />
            <span className="text-sm font-semibold uppercase tracking-wide">Mode 1 — Reveal Word by Word</span>
          </div>

          {/* Verse display */}
          <div className="bg-muted/30 border border-border rounded-xl p-5 min-h-[120px]">
            <div className="flex flex-wrap gap-x-2 gap-y-3">
              {tokens?.map((word, idx) => {
                const revealed = idx < revealedCount;
                return (
                  <span
                    key={idx}
                    className={`inline-block transition-all duration-400 rounded px-1 py-0.5 ${
                      revealed
                        ? 'text-foreground'
                        : 'bg-muted text-transparent select-none'
                    }`}
                    style={{ fontSize: textSizePx, minWidth: `${Math.max(word?.length * 0.6, 1.5)}ch` }}
                  >
                    {revealed ? word : '▓'?.repeat(Math.min(word?.length, 6))}
                  </span>
                );
              })}
            </div>
          </div>

          <p className="text-sm text-muted-foreground text-center">
            {allRevealed
              ? 'All words revealed! Try to recite the verse from memory, then mark complete.'
              : `Tap "Reveal Next" to uncover the next word. Try to say it before revealing!`}
          </p>

          {/* Recitation input (shown when all revealed) */}
          {allRevealed && !isCompleted && (
            <div className="space-y-3">
              <textarea
                value={userInput}
                onChange={e => { setUserInput(e?.target?.value); setInputStatus('idle'); }}
                placeholder="Type the verse from memory to check yourself (optional)…"
                className="w-full h-28 p-4 border border-border rounded-xl bg-input text-foreground resize-none
                  focus:ring-2 focus:ring-ring focus:border-transparent focus:outline-none text-base"
                style={{ fontSize: '16px' }}
              />
              {inputStatus === 'correct' && (
                <div className="flex items-center gap-2 text-success bg-success/10 border border-success/30 rounded-lg px-4 py-3">
                  <Icon name="CheckCircle" size={18} />
                  <span className="font-medium text-sm">Perfect recitation!</span>
                </div>
              )}
              {inputStatus === 'incorrect' && (
                <div className="flex items-center gap-2 text-destructive bg-destructive/10 border border-destructive/30 rounded-lg px-4 py-3">
                  <Icon name="XCircle" size={18} />
                  <span className="font-medium text-sm">Not quite — review the verse and try again.</span>
                </div>
              )}
            </div>
          )}

          {/* Completion state */}
          {isCompleted && (
            <>
              <div className="flex items-center gap-2 text-success bg-success/10 border border-success/30 rounded-lg px-4 py-3">
                <Icon name="CheckCircle" size={18} />
                <span className="font-medium text-sm">Verse complete! Well done.</span>
              </div>
              <AddToLibraryCard />
            </>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-3 mt-auto pb-4">
            {!allRevealed && (
              <>
                <Button
                  variant="outline"
                  onClick={revealAll}
                  iconName="EyeOff"
                  iconPosition="left"
                  className="min-h-[48px] flex-1 sm:flex-none"
                >
                  Reveal All
                </Button>
                <Button
                  variant="default"
                  onClick={revealNext}
                  iconName="Eye"
                  iconPosition="left"
                  className="min-h-[48px] flex-1 sm:flex-none bg-gradient-to-r from-success to-success/80"
                >
                  Reveal Next Word
                </Button>
              </>
            )}
            {allRevealed && !isCompleted && (
              <>
                {userInput?.trim() && inputStatus === 'idle' && (
                  <Button
                    variant="outline"
                    onClick={checkRecitation}
                    iconName="Check"
                    iconPosition="left"
                    className="min-h-[48px] flex-1 sm:flex-none"
                  >
                    Check Recitation
                  </Button>
                )}
                <Button
                  variant="default"
                  onClick={handleFinish}
                  iconName="CheckCircle"
                  iconPosition="left"
                  className="min-h-[48px] flex-1 sm:flex-none bg-gradient-to-r from-success to-success/80"
                >
                  Mark Complete
                </Button>
              </>
            )}
            {isCompleted && (
              <Button
                variant="default"
                onClick={handleNext}
                iconName="ArrowRight"
                iconPosition="right"
                className="min-h-[48px] flex-1 sm:flex-none bg-gradient-to-r from-primary to-primary/80"
              >
                {verseIndex + 1 < totalVerses ? 'Next Verse' : 'Finish'}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Mode 2: Fade to blank ─────────────────────────────────────────────────
  if (subMode === 'blank') {
    const hiddenSet = new Set(hiddenIndices);
    const allHidden = hiddenIndices?.length >= tokens?.length;
    const isReading = stage === 'reading' && hiddenIndices?.length === 0;

    return (
      <div className="min-h-screen bg-background flex flex-col">
        {/* Top bar */}
        <div className="bg-card border-b border-border px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
          <button
            onClick={() => setSubMode(null)}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
          >
            <Icon name="ArrowLeft" size={18} />
          </button>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-foreground">{verse?.reference}</span>
              <span className="text-xs text-muted-foreground">
                {hiddenIndices?.length}/{tokens?.length} hidden
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-1.5">
              <div
                className="bg-primary h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${(hiddenIndices?.length / tokens?.length) * 100}%` }}
              />
            </div>
          </div>
        </div>
        <div className="flex-1 px-4 py-5 flex flex-col gap-5 max-w-2xl mx-auto w-full">
          <div className="flex items-center gap-2 text-primary">
            <Icon name="EyeOff" size={18} />
            <span className="text-sm font-semibold uppercase tracking-wide">Mode 2 — Fade to Blank</span>
          </div>

          {/* Instruction banner */}
          {isReading && (
            <div className="bg-primary/10 border border-primary/30 rounded-xl px-4 py-3 flex items-start gap-3">
              <Icon name="BookOpen" size={18} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
              <p className="text-sm text-foreground">
                Read the full verse below. When you're ready, tap <strong>"Start Removing Words"</strong> — words will be removed one by one until you recite the entire verse from memory.
              </p>
            </div>
          )}

          {/* Verse display */}
          <div className="bg-muted/30 border border-border rounded-xl p-5 min-h-[120px]">
            <div className="flex flex-wrap gap-x-2 gap-y-3">
              {tokens?.map((word, idx) => {
                const hidden = hiddenSet?.has(idx);
                return (
                  <span
                    key={idx}
                    className={`inline-block transition-all duration-400 rounded px-1 py-0.5 ${
                      hidden
                        ? 'bg-muted text-transparent select-none' : 'text-foreground'
                    }`}
                    style={{ fontSize: textSizePx, minWidth: `${Math.max(word?.length * 0.6, 1.5)}ch` }}
                  >
                    {hidden ? '▓'?.repeat(Math.min(word?.length, 6)) : word}
                  </span>
                );
              })}
            </div>
          </div>

          {/* Show full verse toggle (after some words hidden) */}
          {hiddenIndices?.length > 0 && !allHidden && !isCompleted && (
            <button
              onClick={() => setShowFullVerse(v => !v)}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors self-start"
            >
              <Icon name={showFullVerse ? 'EyeOff' : 'Eye'} size={15} />
              {showFullVerse ? 'Hide full verse' : 'Peek at full verse'}
            </button>
          )}
          {showFullVerse && hiddenIndices?.length > 0 && (
            <div className="bg-warning/10 border border-warning/30 rounded-xl p-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Full Verse</p>
              <p className="text-foreground leading-relaxed" style={{ fontSize: textSizePx }}>{cleanedText}</p>
            </div>
          )}

          {/* Recitation input (shown once words start being removed, not after completion) */}
          {hiddenIndices?.length > 0 && !isCompleted && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {allHidden
                  ? 'All words are hidden — recite the entire verse from memory!' :'Recite the full verse (including hidden words) to check yourself:'}
              </p>
              <textarea
                value={userInput}
                onChange={e => { setUserInput(e?.target?.value); setInputStatus('idle'); }}
                placeholder="Type the full verse from memory…"
                className="w-full h-28 p-4 border border-border rounded-xl bg-input text-foreground resize-none
                  focus:ring-2 focus:ring-ring focus:border-transparent focus:outline-none text-base"
                style={{ fontSize: '16px' }}
              />
              {inputStatus === 'correct' && (
                <div className="flex items-center gap-2 text-success bg-success/10 border border-success/30 rounded-lg px-4 py-3">
                  <Icon name="CheckCircle" size={18} />
                  <span className="font-medium text-sm">
                    {allHidden ? '🎉 Amazing! You recited the entire verse from memory!' : 'Correct! Keep going — remove more words.'}
                  </span>
                </div>
              )}
              {inputStatus === 'incorrect' && (
                <div className="flex items-center gap-2 text-destructive bg-destructive/10 border border-destructive/30 rounded-lg px-4 py-3">
                  <Icon name="XCircle" size={18} />
                  <span className="font-medium text-sm">Not quite — review the verse and try again.</span>
                </div>
              )}
            </div>
          )}

          {/* Completion state */}
          {isCompleted && (
            <>
              <div className="flex items-center gap-2 text-success bg-success/10 border border-success/30 rounded-lg px-4 py-3">
                <Icon name="CheckCircle" size={18} />
                <span className="font-medium text-sm">Verse complete! Well done.</span>
              </div>
              <AddToLibraryCard />
            </>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-3 mt-auto pb-4">
            {isReading ? (
              <Button
                variant="default"
                onClick={() => {
                  setStage('reciting');
                  removeNextWord();
                }}
                iconName="Play"
                iconPosition="left"
                className="min-h-[48px] flex-1 bg-gradient-to-r from-primary to-primary/80"
              >
                Start Removing Words
              </Button>
            ) : isCompleted ? (
              <Button
                variant="default"
                onClick={handleNext}
                iconName="ArrowRight"
                iconPosition="right"
                className="min-h-[48px] flex-1 sm:flex-none bg-gradient-to-r from-primary to-primary/80"
              >
                {verseIndex + 1 < totalVerses ? 'Next Verse' : 'Finish'}
              </Button>
            ) : (
              <>
                {userInput?.trim() && inputStatus === 'idle' && (
                  <Button
                    variant="outline"
                    onClick={checkRecitation}
                    iconName="Check"
                    iconPosition="left"
                    className="min-h-[48px] flex-1 sm:flex-none"
                  >
                    Check Recitation
                  </Button>
                )}
                {!allHidden && (
                  <Button
                    variant="outline"
                    onClick={() => { removeNextWord(); setUserInput(''); setInputStatus('idle'); }}
                    iconName="Minus"
                    iconPosition="left"
                    className="min-h-[48px] flex-1 sm:flex-none"
                  >
                    Remove Next Word
                  </Button>
                )}
                {allHidden && (
                  <Button
                    variant="default"
                    onClick={handleFinish}
                    iconName="CheckCircle"
                    iconPosition="left"
                    className="min-h-[48px] flex-1 sm:flex-none bg-gradient-to-r from-success to-success/80"
                  >
                    Mark Complete
                  </Button>
                )}
                {!allHidden && inputStatus === 'correct' && (
                  <Button
                    variant="default"
                    onClick={handleFinish}
                    iconName="CheckCircle"
                    iconPosition="left"
                    className="min-h-[48px] flex-1 sm:flex-none bg-gradient-to-r from-success to-success/80"
                  >
                    Mark Complete
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default ProgressiveRevealMode;
